/**
 * Sprint 2a ad-hook actions: fact check (leak verdict), whale wallet tracker,
 * and emergency averaging-down funds. All are dispatched AFTER a rewarded ad
 * completes (UI is responsible for the ad flow + adHookStats logging).
 */
import type { ActionHandler } from '@app-games/core';
import type { GameState } from './state.js';
import { WHALE_CONFIG } from './config/whale.js';
import {
  COMMENT_CONFIG, NPC_NICKS, REPLY_POOL_BY_MOOD, REPLY_POOL_SIZE, getStance,
} from './config/comments.js';
import type { PendingReply, ReplyMood, InfoState } from './infoState.js';
import { getPnlPercent } from './systems/tradingSystem.js';
import { getRescueCooldownMult } from './config/prestige.js';
import { draw, type BagStore } from './shuffleBag.js';

export const RESCUE_CONFIG = {
  /** hook unlocks when holding PnL is at or below this percent */
  lossThresholdPct: -30,
  /** cash grant = position value * this ratio */
  grantRatio: 0.25,
  /** per-coin cooldown ms (5 min per spec) */
  cooldown: 300_000,
} as const;

export type InfoAction =
  | { type: 'FACTCHECK_LEAK'; payload: { leakId: string } }
  | { type: 'WHALE_TRACK'; payload: { coinId: string } }
  | { type: 'RESCUE_FUNDS'; payload: { coinId: string } }
  | { type: 'POST_NEWS_COMMENT'; payload: { instanceId: string; text: string } }
  | { type: 'PICK_NEWS_STANCE'; payload: { instanceId: string; stanceIdx: number } };

/**
 * Schedule 1~2 NPC reactions 5~15s after the player posts (pure in the bag).
 * A mood biases which reply pool the NPCs draw from (stance banter). The reply
 * text index is DRAWN from a shuffle bag keyed by mood, so a reply line never
 * repeats before that pool is exhausted. Returns the replies + advanced bags.
 */
export function scheduleReplies(
  bags: BagStore, instanceId: string, now: number, mood?: ReplyMood
): { replies: PendingReply[]; bags: BagStore } {
  const replyCount = COMMENT_CONFIG.replyCountMin
    + Math.floor(Math.random() * (COMMENT_CONFIG.replyCountMax - COMMENT_CONFIG.replyCountMin + 1));
  const poolSize = mood ? REPLY_POOL_SIZE : COMMENT_CONFIG.replyPool;
  const key = `npc_reply_${mood ?? 'gen'}`;
  let store = bags;
  const replies: PendingReply[] = [];
  for (let i = 0; i < replyCount; i++) {
    const d = draw(store, key, poolSize);
    store = d.bags;
    replies.push({
      instanceId,
      nickIdx: Math.floor(Math.random() * NPC_NICKS.length),
      replyIdx: d.index,
      mood,
      dueAt: now + COMMENT_CONFIG.replyDelayMin
        + Math.random() * (COMMENT_CONFIG.replyDelayMax - COMMENT_CONFIG.replyDelayMin),
    });
  }
  return { replies, bags: store };
}

/** Append a player comment (trimmed) + pending replies to info (pure). */
function withComment(
  info: InfoState,
  comment: { instanceId: string; text: string; stanceIdx?: number; at: number },
  replies: PendingReply[]
): InfoState {
  return {
    ...info,
    playerComments: [...info.playerComments, comment].slice(-COMMENT_CONFIG.maxStoredComments),
    pendingReplies: [...info.pendingReplies, ...replies],
  };
}

/**
 * Post a one-line player comment on a feed news item and schedule
 * 1~2 NPC reactions 5~15s later (delivered by commentSystem).
 */
export const handlePostNewsComment: ActionHandler<
  GameState,
  Extract<InfoAction, { type: 'POST_NEWS_COMMENT' }>
> = (state, action) => {
  if (!state.info) return null;
  const { instanceId } = action.payload;
  const text = action.payload.text.trim().slice(0, COMMENT_CONFIG.maxLength);
  if (!text) return null;
  if (!state.info.feed.some(f => f.kind === 'news' && f.instanceId === instanceId)) return null;

  const now = Date.now();
  const sched = scheduleReplies(state.info.bags, instanceId, now);
  return {
    ...state,
    info: withComment(
      { ...state.info, bags: sched.bags }, { instanceId, text, at: now }, sched.replies
    ),
  };
};

/**
 * Tap a static stance choice (가즈아 / 손절각 / 관망 …) on a news item.
 * Posts the stance as the player's comment and schedules mood-matched NPC banter.
 * Pure flavor - no market impact.
 */
export const handlePickNewsStance: ActionHandler<
  GameState,
  Extract<InfoAction, { type: 'PICK_NEWS_STANCE' }>
> = (state, action) => {
  if (!state.info) return null;
  const { instanceId, stanceIdx } = action.payload;
  const stance = getStance(stanceIdx);
  if (!stance) return null;
  if (!state.info.feed.some(f => f.kind === 'news' && f.instanceId === instanceId)) return null;

  const now = Date.now();
  const mood = REPLY_POOL_BY_MOOD[stance.mood] as ReplyMood;
  const sched = scheduleReplies(state.info.bags, instanceId, now, mood);
  return {
    ...state,
    info: withComment(
      { ...state.info, bags: sched.bags },
      { instanceId, text: '', stanceIdx, at: now },
      sched.replies
    ),
  };
};

/** Fact check: reveal a pending leak's verdict (ad reward). */
export const handleFactcheckLeak: ActionHandler<
  GameState,
  Extract<InfoAction, { type: 'FACTCHECK_LEAK' }>
> = (state, action) => {
  if (!state.info) return null;
  const { leakId } = action.payload;
  const leak = state.info.feed.find(f => f.kind === 'leak' && f.leakId === leakId);
  if (!leak || leak.kind !== 'leak' || leak.resolved || leak.factChecked) return null;

  return {
    ...state,
    info: {
      ...state.info,
      feed: state.info.feed.map(f =>
        f.kind === 'leak' && f.leakId === leakId ? { ...f, factChecked: true } : f
      ),
    },
    statistics: { ...state.statistics, adsWatched: state.statistics.adsWatched + 1 },
  };
};

/** Whale tracker: briefly reveal the coin's whale phase (ad reward, 2 min cooldown). */
export const handleWhaleTrack: ActionHandler<
  GameState,
  Extract<InfoAction, { type: 'WHALE_TRACK' }>
> = (state, action) => {
  const { coinId } = action.payload;
  const whale = state.whales?.[coinId];
  if (!whale) return null;
  const now = Date.now();
  if (now < whale.trackerCooldownUntil) return null;

  return {
    ...state,
    whales: {
      ...state.whales,
      [coinId]: {
        ...whale,
        trackerRevealUntil: now + WHALE_CONFIG.trackerRevealDuration,
        trackerCooldownUntil: now + WHALE_CONFIG.trackerCooldown,
      },
    },
    statistics: { ...state.statistics, adsWatched: state.statistics.adsWatched + 1 },
  };
};

/** Emergency funds: grant 25% of a deep-loss position's value as cash (ad reward). */
export const handleRescueFunds: ActionHandler<
  GameState,
  Extract<InfoAction, { type: 'RESCUE_FUNDS' }>
> = (state, action) => {
  if (!state.exchange) return null;
  const { coinId } = action.payload;
  const now = Date.now();

  const holding = state.exchange.holdings[coinId];
  const coin = state.exchange.coins[coinId];
  if (!holding || !coin) return null;
  if (getPnlPercent(holding, coin.price) > RESCUE_CONFIG.lossThresholdPct) return null;
  if (now < (state.rescueCooldowns?.[coinId] ?? 0)) return null;

  const grant = holding.amount * coin.price * RESCUE_CONFIG.grantRatio;
  if (grant <= 0) return null;

  // buff_ex_rescue_cd: 물타기 긴급자금 쿨다운 소폭 감소
  const cooldown = RESCUE_CONFIG.cooldown * getRescueCooldownMult(state.prestige.buffs);

  return {
    ...state,
    coins: state.coins + grant,
    totalEarnedThisRun: state.totalEarnedThisRun + grant,
    rescueCooldowns: { ...state.rescueCooldowns, [coinId]: now + cooldown },
    statistics: {
      ...state.statistics,
      totalEarned: state.statistics.totalEarned + grant,
      adsWatched: state.statistics.adsWatched + 1,
    },
  };
};

export const infoActionHandlers = {
  FACTCHECK_LEAK: handleFactcheckLeak,
  WHALE_TRACK: handleWhaleTrack,
  RESCUE_FUNDS: handleRescueFunds,
  POST_NEWS_COMMENT: handlePostNewsComment,
  PICK_NEWS_STANCE: handlePickNewsStance,
} as const;
